import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { BUY_TICKETS_ONLINE, FREE_EVENT, MULTIPLE_PRICING_TIERS, ONE_FLAT_PRICE, REGISTER_OR_RSVP_ONLINE } from '../../schemas/events/eventSchema'
import { EventTypes } from '../../util/constants'
import { EventCategoriesTree } from '../../util/eventCategories'
import { formatPhoneForForm } from '../../util/string'

// One event the flyer reader found (CVL-172/176), as the flyerExtraction query returns it:
// snake_case keys, "" or null for what the flyer does not show.
export type FlyerCandidate = {
  title?: string | null
  description?: string | null
  start_date?: string | null
  start_time?: string | null
  end_date?: string | null
  end_time?: string | null
  all_day?: boolean | null
  is_virtual?: boolean | null
  venue_name?: string | null
  street?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  venue_url?: string | null
  place_id?: number | string | null
  organizer_name?: string | null
  organizer_email?: string | null
  organizer_phone?: string | null
  event_url?: string | null
  ticket_url?: string | null
  registration_required?: boolean | null
  is_free?: boolean | null
  cost_min?: number | null
  cost_max?: number | null
  category?: string | null
  subcategory?: string | null
  age_ranges?: string[] | null
  year_shown?: boolean | null
  year_inferred?: boolean | null
  possible_duplicates?: FlyerDuplicate[] | null
  safety_flagged?: boolean | null
  confidence?: Record<string, string | null | undefined> | null
}

// An event already listed that the flyer's event might be (CVL-176).
export type FlyerDuplicate = { id: number | string; title?: string | null; start_at?: string | null; url?: string | null }

export type FlyerImage = { publicId: string; width?: number | null; height?: number | null }

export type FormChange = [string, unknown]

const TITLE_MAX = 120

// The form fills some fields in by itself as it opens (false, 0, [] and so on), so a field
// counts as changed only when it went from one real value to another.
const blank = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === false ||
  value === 0 ||
  (Array.isArray(value) && value.length === 0)

const sameValue = (a: unknown, b: unknown): boolean => (blank(a) && blank(b)) || isEqual(a, b)

// True when the person changed none of these fields since the form opened.
const untouched = (current: unknown, initial: unknown, keys: string[]): boolean =>
  keys.every(key => sameValue(get(current, key), get(initial, key)))

const present = (value: string | null | undefined): value is string => typeof value === 'string' && value.trim() !== ''

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

const descriptionHtml = (text: string): string =>
  text
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(paragraph => paragraph !== '')
    .map(paragraph => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('')

// "2027-07-04" and "19:30" as a local Date; no time gives the start of the day.
const localDate = (date: string, time?: string | null): Date | null => {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim())
  if (!day) return null
  const clock = present(time) ? /^(\d{1,2}):(\d{2})/.exec(time.trim()) : null
  const [, year, month, dayOfMonth] = day
  return new Date(Number(year), Number(month) - 1, Number(dayOfMonth), clock ? Number(clock[1]) : 0, clock ? Number(clock[2]) : 0)
}

const scheduleChanges = (candidate: FlyerCandidate): FormChange[] => {
  if (!present(candidate.start_date)) return []
  const startAt = localDate(candidate.start_date, candidate.start_time)
  if (!startAt) return []
  const hasStartTime = present(candidate.start_time)
  const hasEndTime = present(candidate.end_time)
  const endAt = (hasEndTime && localDate(candidate.end_date || candidate.start_date, candidate.end_time)) || startAt

  return [
    ['startAt', startAt],
    ['endAt', endAt],
    // A time the flyer does not show stays blank for the person to pick, never a made-up one.
    ['startAtTimeBlank', !hasStartTime],
    ['endAtTimeBlank', !hasEndTime],
    ['allDay', candidate.all_day === true],
  ]
}

const venueChanges = (candidate: FlyerCandidate): FormChange[] => {
  if (!present(candidate.venue_name)) return []
  if (candidate.place_id !== null && candidate.place_id !== undefined && candidate.place_id !== '') {
    return [
      ['useTempPlace', false],
      ['placeId', `gid://civiclift/Place/${candidate.place_id}`],
      ['placeName', candidate.venue_name],
    ]
  }

  const changes: FormChange[] = [
    ['useTempPlace', true],
    ['tempPlace.name', candidate.venue_name],
  ]
  if (present(candidate.venue_url)) changes.push(['tempPlace.url', candidate.venue_url])
  const address: [string, string | null | undefined][] = [
    ['streetAddress', candidate.street],
    ['city', candidate.city],
    ['state', candidate.state],
    ['zipCode', candidate.zip],
  ]
  address.forEach(([key, value]) => {
    if (present(value)) changes.push([`tempPlace.address.${key}`, value])
  })
  return changes
}

const organizerChanges = (candidate: FlyerCandidate): FormChange[] => {
  if (![candidate.organizer_name, candidate.organizer_email, candidate.organizer_phone].some(present)) return []
  // The same as picking "Someone else", which clears the organizer fields, so they come after it.
  return [
    ['organizerType', 'other'],
    ['submitterOwnsEvent', false],
    ['organizer.name', candidate.organizer_name || ''],
    ['organizer.email', candidate.organizer_email || ''],
    ['organizer.phone', formatPhoneForForm(candidate.organizer_phone)],
  ]
}

const categoryChanges = (candidate: FlyerCandidate): FormChange[] => {
  const category = candidate.category
  if (!present(category) || !EventCategoriesTree[category]) return []
  const changes: FormChange[] = [['category', category]]
  const subcategories = EventCategoriesTree[category].map(subcategory => subcategory.split(' (')[0])
  if (present(candidate.subcategory) && subcategories.includes(candidate.subcategory)) {
    changes.push(['subcategory', candidate.subcategory])
  }
  return changes
}

const costChanges = (candidate: FlyerCandidate): FormChange[] => {
  if (candidate.is_free === true) return [['costTier', FREE_EVENT]]
  const min = candidate.cost_min ?? candidate.cost_max
  const max = candidate.cost_max ?? candidate.cost_min
  if (typeof min !== 'number' || typeof max !== 'number') return []
  if (min === max) return [['costTier', ONE_FLAT_PRICE], ['costMin', min]]
  return [
    ['costTier', MULTIPLE_PRICING_TIERS],
    ['costMin', Math.min(min, max)],
    ['costMax', Math.max(min, max)],
  ]
}

const ticketChanges = (candidate: FlyerCandidate): FormChange[] => {
  if (!present(candidate.ticket_url)) return []
  const howToAttend = candidate.registration_required === true || candidate.is_free === true ? REGISTER_OR_RSVP_ONLINE : BUY_TICKETS_ONLINE
  return [
    ['needTicketOrRsvp', howToAttend],
    ['ticketOrRsvpUrl', candidate.ticket_url],
  ]
}

// The flyer as the event image, unless the person already picked one.
export const flyerImageChanges = (image: FlyerImage | null | undefined, currentModel: unknown, initialModel: unknown): FormChange[] => {
  if (!image?.publicId || !untouched(currentModel, initialModel, ['image.publicId'])) return []
  return [['image', { publicId: image.publicId, width: image.width ?? null, height: image.height ?? null }]]
}

// The form changes one flyer read makes, in the order to apply them. A group of fields the
// person already changed since the form opened is left exactly as they have it.
export const flyerCandidateToFormChanges = (
  candidate: FlyerCandidate,
  currentModel: unknown,
  initialModel: unknown,
  image?: FlyerImage | null,
): FormChange[] => {
  const changes: FormChange[] = []
  const fill = (keys: string[], groupChanges: FormChange[]) => {
    if (groupChanges.length && untouched(currentModel, initialModel, keys)) changes.push(...groupChanges)
  }

  // The When section stays hidden until the event type is set.
  fill(['eventType'], [['eventType', EventTypes.SINGLE_EVENT]])
  if (present(candidate.title)) fill(['title'], [['title', candidate.title.trim().slice(0, TITLE_MAX)]])
  if (present(candidate.description)) fill(['description'], [['description', descriptionHtml(candidate.description)]])
  fill(['startAt', 'endAt', 'allDay'], scheduleChanges(candidate))
  fill(['useTempPlace', 'placeId', 'tempPlace'], venueChanges(candidate))
  if (candidate.is_virtual === true) {
    const online: FormChange[] = [['isVirtual', true]]
    if (present(candidate.event_url)) online.push(['url', candidate.event_url])
    fill(['isVirtual', 'url'], online)
  } else if (present(candidate.event_url)) {
    fill(['url'], [['url', candidate.event_url]])
  }
  // Only while the organizer is still the person themselves, the form's starting choice.
  const organizerType = get(currentModel, 'organizerType')
  if (!organizerType || organizerType === 'myself') {
    fill(['organizerType', 'selectedOrganizationId', 'submitterOwnsEvent', 'organizer'], organizerChanges(candidate))
  }
  fill(['category', 'subcategory'], categoryChanges(candidate))
  if (candidate.confidence?.age_ranges === 'high' && candidate.age_ranges?.length) {
    fill(['ageRanges'], [['ageRanges', candidate.age_ranges]])
  }
  fill(['costTier', 'costMin', 'costMax'], costChanges(candidate))
  fill(['needTicketOrRsvp', 'ticketOrRsvpUrl'], ticketChanges(candidate))
  changes.push(...flyerImageChanges(image, currentModel, initialModel))

  return changes
}

// What the person is asked to check once a flyer filled in the form (CVL-177).
export type FlyerMarkKey =
  | 'title'
  | 'description'
  | 'when'
  | 'year'
  | 'venue'
  | 'organizer.name'
  | 'organizer.email'
  | 'organizer.phone'
  | 'category'
  | 'ageRanges'
  | 'cost'
  | 'tickets'

export type FlyerMarkStrength = 'low' | 'normal'

export type FlyerMarks = {
  // The fields the flyer filled in, and how strongly to highlight each.
  checks: Partial<Record<FlyerMarkKey, FlyerMarkStrength>>
  notOnFlyer: { time: boolean; venue: boolean }
  assumedYear: number | null
  // The flyer filled in the date, so it shows in words.
  dateFilled: boolean
}

// The marker each filled-in form field belongs to, and the reader's confidence keys behind it.
// The image and the event type are filled in too, but have no marker.
const MARKS: [FlyerMarkKey, RegExp, string[]][] = [
  ['title', /^title$/, ['title']],
  ['description', /^description$/, ['description']],
  ['when', /^(startAt|endAt|startAtDate|startAtTime|endAtTime|allDay|startAtTimeBlank|endAtTimeBlank)$/, ['start_date', 'start_time', 'end_date', 'end_time', 'all_day']],
  ['year', /^(startAt|startAtDate)$/, []],
  ['venue', /^(placeId|placeName|useTempPlace|tempPlace(\..*)?|isVirtual|url)$/, ['venue_name', 'street', 'city', 'state', 'zip', 'is_virtual', 'event_url']],
  ['organizer.name', /^organizer\.name$/, ['organizer_name']],
  ['organizer.email', /^organizer\.email$/, ['organizer_email']],
  ['organizer.phone', /^organizer\.phone$/, ['organizer_phone']],
  ['category', /^(category|subcategory)$/, ['category', 'subcategory']],
  ['ageRanges', /^ageRanges$/, ['age_ranges']],
  ['cost', /^(costTier|costMin|costMax)$/, ['is_free', 'cost_min', 'cost_max']],
  ['tickets', /^(needTicketOrRsvp|ticketOrRsvpUrl)$/, ['ticket_url', 'registration_required']],
]

// The markers a change to this form field clears.
export const flyerMarkKeysFor = (formKey: string): FlyerMarkKey[] =>
  MARKS.filter(([, fields]) => fields.test(formKey)).map(([key]) => key)

// The markers for the changes a flyer read made.
export const flyerMarks = (candidate: FlyerCandidate, applied: FormChange[]): FlyerMarks => {
  const checks: FlyerMarks['checks'] = {}
  applied.forEach(([formKey, value]) => {
    // An organizer detail the flyer does not show is cleared, not filled in.
    if (formKey.startsWith('organizer.') && blank(value)) return
    flyerMarkKeysFor(formKey).forEach(key => {
      const confidenceKeys = MARKS.find(([markKey]) => markKey === key)?.[2] || []
      const low = confidenceKeys.some(field => candidate.confidence?.[field] === 'low')
      checks[key] = low ? 'low' : 'normal'
    })
  })

  const dateFilled = applied.some(([formKey]) => formKey === 'startAt')
  const year = dateFilled && candidate.year_inferred === true && present(candidate.start_date) ? Number(candidate.start_date.slice(0, 4)) : null
  if (!year) delete checks.year

  return {
    checks,
    notOnFlyer: {
      time: candidate.all_day !== true && !present(candidate.start_time),
      venue: candidate.is_virtual !== true && !present(candidate.venue_name),
    },
    assumedYear: year,
    dateFilled,
  }
}

// The markers left once the person changed this form field.
export const flyerMarksAfterChange = (marks: FlyerMarks | null, formKey: string): FlyerMarks | null => {
  if (!marks) return marks
  const cleared = flyerMarkKeysFor(formKey).filter(key => marks.checks[key])
  const time = marks.notOnFlyer.time && !/^(startAtTime|allDay)$/.test(formKey)
  const venue = marks.notOnFlyer.venue && !flyerMarkKeysFor(formKey).includes('venue')
  if (!cleared.length && time === marks.notOnFlyer.time && venue === marks.notOnFlyer.venue) return marks
  const checks = { ...marks.checks }
  cleared.forEach(key => delete checks[key])
  return { ...marks, checks, notOnFlyer: { time, venue } }
}
